import { stopChatFleetSync } from "./app-fleet-sync.ts";
import { connectGateway } from "./app-gateway.ts";
import {
  startLlmStatsCapacityPolling,
  startLogsPolling,
  startNodesPolling,
  stopLlmStatsCapacityPolling,
  stopLogsPolling,
  stopNodesPolling,
  startDebugPolling,
  stopDebugPolling,
  startAgentsPolling,
  stopAgentsPolling,
  startSubagentsPolling,
  stopSubagentsPolling,
  startLiveSessionsPolling,
  stopChatPolling,
  syncChatPolling,
} from "./app-polling.ts";
import { observeTopbar, scheduleChatScroll, scheduleLogsScroll } from "./app-scroll.ts";
import {
  applySettingsFromUrl,
  attachThemeListener,
  detachThemeListener,
  inferBasePath,
  syncTabWithLocation,
  syncThemeWithSettings,
} from "./app-settings.ts";
import { buildAgentRoster } from "./chat/agent-roster-model.ts";
import {
  fetchArtifactSummaryForPending,
  resolveFileChangesNotice,
  resolveFleetFileChangesRollup,
} from "./chat/chat-file-changes-notice.ts";
import { refreshChatLastRunFiles, setChatLastRunSnapshot } from "./chat/chat-last-run-files.ts";
import { syncChatStalenessTicker, stopChatStalenessTicker } from "./chat/chat-sync-ticker.ts";
import { stopChatWaitTicker, syncChatWaitTicker } from "./chat/chat-wait-ticker.ts";
import { applyDocumentTitle, resolveDocumentTitle } from "./chat/document-title.ts";
import { isFleetAutoInjectSuppressed } from "./chat/fleet-auto-inject-guard.ts";
import {
  evaluateFleetCompletionNotice,
  pickFleetFailedChild,
} from "./chat/fleet-completion-notice.ts";
import {
  enrichCardsWithJobChangedPaths,
  fetchSelfImprovementJobs,
  indexSelfImprovementJobsByIssue,
  indexSelfImprovementJobsByWorkerSession,
} from "./chat/fleet-subagent-changed-paths.ts";
import { resolveAgentIdForSession } from "./chat/resolve-session-agent.ts";
import { syncSubagentTabRunning, type SessionTabActivityMap } from "./chat/session-tab-activity.ts";
import { injectChatMessage } from "./controllers/chat.ts";
import {
  stopFleetWorkPlanPolling,
  syncFleetWorkPlanPolling,
} from "./controllers/fleet-work-plan.ts";
import { isFleetConsoleTab, type Tab } from "./navigation.ts";
import type { AgentsListResult, SessionsListResult } from "./types.ts";
import type { AgentRosterItem } from "./ui-types.ts";
import type { ChatSubagentCard } from "./ui-types.ts";

type LifecycleHost = {
  basePath: string;
  tab: Tab;
  settings: { token: string; fleetAutoJumpToFailedChild?: boolean };
  password: string;
  chatHasAutoScrolled: boolean;
  chatLoading: boolean;
  chatTranscriptLoading?: boolean;
  chatMessages: unknown[];
  chatToolMessages: unknown[];
  chatStream: string;
  logsAutoFollow: boolean;
  logsAtBottom: boolean;
  logsEntries: unknown[];
  popStateHandler: () => void;
  topbarObserver: ResizeObserver | null;
  agentsList: AgentsListResult | null;
  sessionKey: string;
  /** Chat-history catalog — the source of the active chat's display title. */
  sessionsResult?: SessionsListResult | null;
  chatSubagents: ChatSubagentCard[];
  chatRunId: string | null;
  chatSending: boolean;
  agentRoster: AgentRosterItem[];
  flociAgents?: import("./controllers/floci.ts").FlociAgentRecord[];
  sessionTabActivity?: SessionTabActivityMap;
  cacheActiveChatMessages?: () => void;
  connected?: boolean;
  refreshSessionEnvironment?: () => Promise<void>;
  /** Gateway client — stopped on disconnect so its timers and listeners go. */
  client?: { stop: () => void } | null;
  /** Fleet Control chat staleness verification — 1s tick handle. */
  chatStalenessTicker?: number | null;
  /** Lit lifecycle hook — needed by the staleness ticker to trigger pill re-render. */
  requestUpdate?: () => void;
};

async function maybeInjectFileChangesNotices(host: LifecycleHost) {
  const app = host as unknown as {
    sessionKey: string;
    chatRunId: string | null;
    chatSending: boolean;
    chatLoading?: boolean;
    chatFleetAutoInjectHeld?: boolean;
    chatRunArtifactsSinceTs: Record<string, number>;
    fileChangesNotifiedFingerprint: Record<string, string>;
    fleetFileChangesNotifiedFingerprint: Record<string, string>;
    chatSubagents: ChatSubagentCard[];
    chatMessages?: unknown[];
    connected?: boolean;
    client?: import("./gateway.ts").GatewayBrowserClient | null;
  };
  if (!app.connected || !app.client || isFleetAutoInjectSuppressed(app)) {
    return;
  }

  const perSession = await resolveFileChangesNotice({
    sessionKey: app.sessionKey,
    chatRunId: app.chatRunId,
    chatSending: app.chatSending,
    client: app.client,
    connected: app.connected,
    chatRunArtifactsSinceTs: app.chatRunArtifactsSinceTs,
    fileChangesNotifiedFingerprint: app.fileChangesNotifiedFingerprint,
    fleetFileChangesNotifiedFingerprint: app.fleetFileChangesNotifiedFingerprint,
    chatSubagents: app.chatSubagents,
    chatMessages: app.chatMessages,
  });
  if (perSession.shouldInject && perSession.message && perSession.fingerprint) {
    const ok = await injectChatMessage(
      app as unknown as Parameters<typeof injectChatMessage>[0],
      perSession.message,
      perSession.label,
    );
    if (ok) {
      const sessionKey = app.sessionKey.trim();
      app.fileChangesNotifiedFingerprint = {
        ...app.fileChangesNotifiedFingerprint,
        [sessionKey]: perSession.fingerprint,
      };
      const { populatePendingReviewFromSummary, refreshChatCheckpoints } = await import(
        /* @vite-ignore */ "./app-chat-cursor-parity.ts"
      );
      const summary = await fetchArtifactSummaryForPending(
        app as unknown as Parameters<typeof fetchArtifactSummaryForPending>[0],
      );
      if (summary?.files?.length) {
        const openClawApp = app as unknown as import("./app.ts").OpenClawApp;
        populatePendingReviewFromSummary(
          openClawApp,
          summary.files.map((f) => f.changeId).filter((id): id is string => Boolean(id)),
        );
        setChatLastRunSnapshot(openClawApp, sessionKey, summary);
        void refreshChatCheckpoints(openClawApp);
      }
    }
  }

  if (!app.chatRunId && !app.chatSending) {
    await refreshChatLastRunFiles(app as unknown as import("./app.ts").OpenClawApp);
  }

  const fleetRollup = await resolveFleetFileChangesRollup({
    sessionKey: app.sessionKey,
    chatRunId: app.chatRunId,
    chatSending: app.chatSending,
    client: app.client,
    connected: app.connected,
    chatRunArtifactsSinceTs: app.chatRunArtifactsSinceTs,
    fileChangesNotifiedFingerprint: app.fileChangesNotifiedFingerprint,
    fleetFileChangesNotifiedFingerprint: app.fleetFileChangesNotifiedFingerprint,
    chatSubagents: app.chatSubagents,
    chatMessages: app.chatMessages,
  });
  if (fleetRollup.shouldInject && fleetRollup.message && fleetRollup.fingerprint) {
    const ok = await injectChatMessage(
      app as unknown as Parameters<typeof injectChatMessage>[0],
      fleetRollup.message,
      fleetRollup.label,
    );
    if (ok) {
      const sessionKey = app.sessionKey.trim();
      app.fleetFileChangesNotifiedFingerprint = {
        ...app.fleetFileChangesNotifiedFingerprint,
        [sessionKey]: fleetRollup.fingerprint,
      };
    }
  }
}

async function maybeInjectFleetCompletionNotice(host: LifecycleHost) {
  const app = host as unknown as {
    sessionKey: string;
    chatRunId: string | null;
    chatSending: boolean;
    chatLoading?: boolean;
    chatFleetAutoInjectHeld?: boolean;
    chatSubagents: ChatSubagentCard[];
    chatMessages?: unknown[];
    orchestrationReportsDone: Record<string, boolean>;
    fleetCompletionNotifiedFingerprint: Record<string, string>;
    connected?: boolean;
    client?: import("./gateway.ts").GatewayBrowserClient | null;
  };
  if (!app.connected || !app.client || isFleetAutoInjectSuppressed(app)) {
    return;
  }
  const agentId = resolveAgentIdForSession(app.sessionKey);
  const jobs = await fetchSelfImprovementJobs(app.client, agentId);
  const enrichedSubagents = enrichCardsWithJobChangedPaths(
    app.chatSubagents,
    indexSelfImprovementJobsByWorkerSession(jobs),
    indexSelfImprovementJobsByIssue(jobs),
  );
  const decision = evaluateFleetCompletionNotice({
    sessionKey: app.sessionKey,
    chatRunId: app.chatRunId,
    chatSending: app.chatSending,
    chatSubagents: enrichedSubagents,
    chatMessages: app.chatMessages,
    orchestrationReportsDone: app.orchestrationReportsDone,
    fleetCompletionNotifiedFingerprint: app.fleetCompletionNotifiedFingerprint,
  });
  if (!decision.shouldInject || !decision.message || !decision.fingerprint) {
    // JAISIU-3319 / Pillar H: even when no notice will be injected (e.g.
    // the fleet just settled without the headline formatting, or
    // dedupe-stamped this session), still consider auto-jumping to the
    // failing child if the summary went red. This is a UX affordance, not
    // a follow-up RPC; it must respect existing chatTabMeta so the parent
    // chrome stays authoritative.
    maybeAutoJumpToFailingChildImpl(host, enrichedSubagents, app.sessionKey);
    return;
  }
  // Lock fingerprint before await so concurrent handleUpdated ticks cannot double-inject.
  const sessionKey = app.sessionKey.trim();
  app.fleetCompletionNotifiedFingerprint = {
    ...app.fleetCompletionNotifiedFingerprint,
    [sessionKey]: decision.fingerprint,
  };
  maybeAutoJumpToFailingChildImpl(host, enrichedSubagents, sessionKey);
  const ok = await injectChatMessage(
    app as unknown as Parameters<typeof injectChatMessage>[0],
    decision.message,
    "fleet-complete",
  );
  if (!ok) {
    const next = { ...app.fleetCompletionNotifiedFingerprint };
    delete next[sessionKey];
    app.fleetCompletionNotifiedFingerprint = next;
  }
}

/**
 * JAISIU-3319 / Pillar H: when a fan-out settles with `needs_attention`,
 * auto-jump to the first failed/cancelled child so the operator reads the
 * guardrail context without manually opening tabs. Idempotent: only fires
 * once per (sessionKey, childSessionKey) pair; uses the existing
 * `chatTabMeta` authoritativeness that Pillar C established.
 *
 * JAISIU-fleet-autojump-default-off — gated behind `fleetAutoJumpToFailedChild`
 * setting which defaults to `false`. I5 (parent session stays active during
 * fan-out) means the operator must explicitly open the child tab unless they
 * have opted in. Without this gate the operator's tab would jump to a
 * failing child session without consent, which is the operator-visible
 * Phase 4.3 symptom.
 */
/** Exported for tests; production callers invoke this directly. */
export function maybeAutoJumpToFailingChildImpl(
  host: LifecycleHost,
  cards: ChatSubagentCard[],
  sessionKey: string,
): void {
  const app = host as unknown as {
    settings?: { fleetAutoJumpToFailedChild?: boolean };
    openSubagentChatTab?: (params: {
      childKey: string;
      parentKey: string;
      label?: string;
      task?: string;
      runId?: string;
    }) => void;
    chatTabMeta?: Record<string, import("./views/chat-session-context.ts").ChatSessionContextMeta>;
    fleetAutoJumpedChild?: Record<string, string>;
  };
  const trimmedSession = sessionKey.trim();
  if (!trimmedSession) return;
  // I5 — operator opt-in only.
  if (app.settings?.fleetAutoJumpToFailedChild !== true) {
    return;
  }
  if (typeof app.openSubagentChatTab !== "function") return;
  const target = pickFleetFailedChild(cards);
  if (!target?.childSessionKey) return;
  if (app.fleetAutoJumpedChild?.[trimmedSession] === target.childSessionKey) {
    return;
  }
  app.openSubagentChatTab({
    childKey: target.childSessionKey,
    parentKey: trimmedSession,
    label: target.label,
    task: target.task,
    runId: target.runId,
  });
  app.fleetAutoJumpedChild = {
    ...(app.fleetAutoJumpedChild ?? {}),
    [trimmedSession]: target.childSessionKey,
  };
}

const orchestrationReportInflight = new Set<string>();

async function maybePersistOrchestrationReports(host: LifecycleHost) {
  const app = host as unknown as {
    chatSubagents: ChatSubagentCard[];
    orchestrationReportsDone: Record<string, boolean>;
    sessionKey: string;
    chatLoading?: boolean;
    chatFleetAutoInjectHeld?: boolean;
    connected?: boolean;
    client?: import("./gateway.ts").GatewayBrowserClient | null;
  };
  if (!app.connected || !app.client || isFleetAutoInjectSuppressed(app)) {
    return;
  }
  const byOrch = new Map<string, ChatSubagentCard[]>();
  for (const card of app.chatSubagents) {
    if (!card.orchestrationId) {
      continue;
    }
    const list = byOrch.get(card.orchestrationId) ?? [];
    list.push(card);
    byOrch.set(card.orchestrationId, list);
  }
  for (const [id, cards] of byOrch) {
    if (app.orchestrationReportsDone[id] || orchestrationReportInflight.has(id)) {
      continue;
    }
    const allSettled = cards.every(
      (c) => c.status === "done" || c.status === "error" || c.status === "cancelled",
    );
    if (!allSettled) {
      continue;
    }
    const awaitingPreview = cards.some(
      (c) =>
        (c.status === "done" || c.status === "error") &&
        !c.outputPreview?.trim() &&
        !c.streamText?.trim() &&
        !c.error?.trim(),
    );
    if (awaitingPreview) {
      continue;
    }
    orchestrationReportInflight.add(id);
    const lines = cards.map((c) => {
      const preview =
        c.outputPreview?.trim() ||
        c.streamText?.trim().slice(0, 200) ||
        c.error?.trim().slice(0, 120) ||
        (c.status === "cancelled" ? "cancelled" : "(no deliverable)");
      const statusLabel =
        c.status === "done" && preview === "(no deliverable)" ? "done (no deliverable)" : c.status;
      return `- **${c.label}** (${statusLabel}): ${preview}`;
    });
    const body = `## Orchestration ${id}\n\n${lines.join("\n")}`;
    try {
      const ok = await injectChatMessage(
        app as unknown as Parameters<typeof injectChatMessage>[0],
        body,
        "orchestration",
      );
      if (ok) {
        app.orchestrationReportsDone = { ...app.orchestrationReportsDone, [id]: true };
      }
    } finally {
      orchestrationReportInflight.delete(id);
    }
  }
}

export function handleConnected(host: LifecycleHost) {
  syncChatWaitTicker(host as unknown as Parameters<typeof syncChatWaitTicker>[0]);
  host.basePath = inferBasePath();
  applySettingsFromUrl(host as unknown as Parameters<typeof applySettingsFromUrl>[0]);
  syncTabWithLocation(host as unknown as Parameters<typeof syncTabWithLocation>[0], true);
  syncThemeWithSettings(host as unknown as Parameters<typeof syncThemeWithSettings>[0]);
  attachThemeListener(host as unknown as Parameters<typeof attachThemeListener>[0]);
  window.addEventListener("popstate", host.popStateHandler);
  // Clear stale answered-question persistence. Without this, a previously
  // answered card would reappear on every fresh page load as a gray-out
  // bubble stuck to the bottom of the chat thread. The operator wanted
  // the chat to behave like a plain conversation with no visible
  // questionnaire chrome, so we drop the cache on app start.
  clearStaleFleetAnswerPersistence();
  connectGateway(host as unknown as Parameters<typeof connectGateway>[0]);
  // Polls are tab-gated: each one is only useful while its consumer tab/panel
  // is mounted. Starting them unconditionally on every page load (including
  // the chat tab, where none are needed) caused ~5s RPC + render churn.
  // `setTab` in app-settings.ts starts/stops each poll on tab change.
  if (host.tab === "agents") {
    startAgentsPolling(host as unknown as Parameters<typeof startAgentsPolling>[0]);
  }
  syncFleetWorkPlanPolling(
    host as unknown as Parameters<typeof syncFleetWorkPlanPolling>[0],
    host.tab === "agents" &&
      (host as unknown as { agentsPanel?: string }).agentsPanel === "workplan",
  );
  if (host.tab === "instances") {
    startNodesPolling(host as unknown as Parameters<typeof startNodesPolling>[0]);
  }
  if (host.tab === "employees") {
    startLiveSessionsPolling(host as unknown as Parameters<typeof startLiveSessionsPolling>[0]);
  }
  // Subagents power the chat-composer sidebar (chat tab) and the subagents
  // fleet panel. Refreshed on every chat final event via
  // `refreshSubagentRegistry`; 30s polling is only needed while a tab that
  // displays them is mounted.
  if (isFleetConsoleTab(host.tab)) {
    startSubagentsPolling(host as unknown as Parameters<typeof startSubagentsPolling>[0]);
  }
  if (host.tab === "logs") {
    startLogsPolling(host as unknown as Parameters<typeof startLogsPolling>[0]);
  }
  if (host.tab === "debug") {
    startDebugPolling(host as unknown as Parameters<typeof startDebugPolling>[0]);
  }
  if (host.tab === "llm-stats" || host.tab === "llm") {
    startLlmStatsCapacityPolling(
      host as unknown as Parameters<typeof startLlmStatsCapacityPolling>[0],
    );
  }
  syncChatPolling(host as unknown as Parameters<typeof syncChatPolling>[0]);
  // Fleet Control staleness verification — start the 1s pill ticker.
  // The body is a no-op while `isChatStale` reports fresh, so the cost
  // is one O(1) call per second when everything is healthy.
  syncChatStalenessTicker(host as unknown as Parameters<typeof syncChatStalenessTicker>[0]);
}

export function handleFirstUpdated(host: LifecycleHost) {
  observeTopbar(host as unknown as Parameters<typeof observeTopbar>[0]);

  // Per spec: the chat-history sidebar must show its full list as soon as
  // the host element finishes its first render. We previously relied on
  // the `connected` watcher (in `handleUpdated`) to trigger the first
  // fetch — but Vite HMR may update a child component without
  // re-firing `connected`, leaving `state.sessionsResult` stale or
  // empty and the sidebar showing "No chats yet" until a hard reload.
  // The `firstUpdated` lifecycle hook always runs after the first paint
  // of the host element, including after an HMR remount, so kicking
  // off the refresh here means a fresh page (or an HMR reload) starts
  // with a populated sidebar without any manual cache clear.
  void (
    host as unknown as { refreshChatHistorySessions?: () => Promise<void> }
  ).refreshChatHistorySessions?.();

  // JAISIU-3474 — bootstrap the active chat. The `onConnect` handler
  // in app-gateway.ts also fires `loadChatHistory`, but it runs in a
  // window between WS-open and Lit's first paint; on a slow WS or an
  // HMR remount the chat view can render briefly with an empty
  // transcript (or only the streaming tail) before the history RPC
  // resolves. Calling `loadChatHistory` here too is idempotent
  // (the loader has an in-flight guard) and guarantees the active
  // chat's transcript begins loading the moment the host element is
  // mounted. The same call also kicks off the scroll-anchor lookup
  // so the bootstrap can drain older pages and restore the
  // operator's last-viewed message.
  const bootHost = host as unknown as {
    tab?: string;
    sessionKey?: string;
    client?: {
      request: (method: string, params: Record<string, unknown>) => Promise<unknown>;
    } | null;
    connected?: boolean;
    loadChatHistory?: () => Promise<unknown>;
    loadScrollAnchorForActiveSession?: () => Promise<unknown>;
    chatMessages?: unknown[];
  };
  if (bootHost.tab === "chat" && bootHost.sessionKey?.trim()) {
    // Load the scroll anchor FIRST so the history bootstrap can
    // decide whether to drain older pages. The lookup is async
    // against IDB; await it before kicking off the history load so
    // the restore decision has the anchor in hand by the time the
    // first `chat.history` response lands.
    void (async () => {
      await bootHost.loadScrollAnchorForActiveSession?.();
      if (bootHost.client && bootHost.connected) {
        // WS already connected — await the history load so the
        // restore path runs in lock-step with the initial paint.
        // This is the only place in the app where we BLOCK the
        // bootstrap on `loadChatHistory`: every other call site
        // fires-and-forgets because the polling tick will catch up
        // shortly. Here we need determinism because the restore is
        // racy with the next render and a delayed load would leave
        // the operator staring at an empty transcript for several
        // hundred ms.
        await bootHost.loadChatHistory?.();
        if (
          (bootHost as unknown as { sessionKey?: string }).sessionKey?.trim() ===
          bootHost.sessionKey?.trim()
        ) {
          // Restore must run AFTER `loadChatHistory` (so the
          // anchor can be located in the rendered window) and AFTER
          // Lit commits the DOM (so `offsetTop` is stable). The
          // helper handles both ordering requirements internally.
          const { restorePersistedScrollAnchor } = await import(
            /* @vite-ignore */ "./app-chat-cursor-parity.ts"
          );
          await restorePersistedScrollAnchor(
            bootHost as unknown as Parameters<typeof restorePersistedScrollAnchor>[0],
          );
        }
      }
    })();
  }

  // Wire up Ctrl+M for multi-agent switcher
  const app = host as unknown as {
    connected?: boolean;
    client?: {
      request: (method: string, params: Record<string, unknown>) => Promise<unknown>;
    } | null;
    openFleetMultiAgentSwitcher?: () => void;
    fleetMultiAgentSwitcherOpen?: boolean;
    fleetProfiles?: unknown[];
    loadFleetProfiles?: () => Promise<void>;
    closeFleetMultiAgentSwitcher?: () => void;
    openGlobalSearch?: () => void;
    closeGlobalSearch?: () => void;
    globalSearchOpen?: boolean;
  };

  window.addEventListener("keydown", (e: KeyboardEvent) => {
    if (!app.connected) return;
    // Ctrl+M or Cmd+M
    if ((e.ctrlKey || e.metaKey) && e.key === "m" && !e.shiftKey && !e.altKey) {
      e.preventDefault();
      e.stopPropagation();
      if (app.fleetMultiAgentSwitcherOpen) {
        app.closeFleetMultiAgentSwitcher?.();
      } else {
        app.openFleetMultiAgentSwitcher?.();
      }
      return;
    }
    // Ctrl+K or Cmd+K — UX-R7 / JAISIU-3297 global search shortcut.
    // Declared in keyboard-shortcuts.ts but never bound; we wire it here.
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k" && !e.shiftKey && !e.altKey) {
      e.preventDefault();
      e.stopPropagation();
      if (app.globalSearchOpen) {
        app.closeGlobalSearch?.();
      } else {
        app.openGlobalSearch?.();
      }
    }
  });
}

export function handleDisconnected(host: LifecycleHost) {
  stopChatWaitTicker(host as unknown as Parameters<typeof stopChatWaitTicker>[0]);
  stopChatStalenessTicker(host as unknown as Parameters<typeof stopChatStalenessTicker>[0]);
  stopChatFleetSync();
  window.removeEventListener("popstate", host.popStateHandler);
  stopAgentsPolling(host as unknown as Parameters<typeof stopAgentsPolling>[0]);
  stopFleetWorkPlanPolling(host as unknown as Parameters<typeof stopFleetWorkPlanPolling>[0]);
  stopSubagentsPolling(host as unknown as Parameters<typeof stopSubagentsPolling>[0]);
  stopNodesPolling(host as unknown as Parameters<typeof stopNodesPolling>[0]);
  stopLogsPolling(host as unknown as Parameters<typeof stopLogsPolling>[0]);
  stopLlmStatsCapacityPolling(host as unknown as Parameters<typeof stopLlmStatsCapacityPolling>[0]);
  stopDebugPolling(host as unknown as Parameters<typeof stopDebugPolling>[0]);
  stopChatPolling(host as unknown as Parameters<typeof stopChatPolling>[0]);
  detachThemeListener(host as unknown as Parameters<typeof detachThemeListener>[0]);
  host.topbarObserver?.disconnect();
  host.topbarObserver = null;
  // Tear the gateway client down too. A removed <jaisiu-app> previously left
  // a live 25s heartbeat, a reconnect timer, and the visibility/freeze/resume
  // listeners running — each retaining the client, and through it
  // `opts.onEvent`, the whole app element graph. Repeated mounts (HMR, or a
  // future remount path) therefore compounded.
  host.client?.stop();
}

export function handleUpdated(host: LifecycleHost, changed: Map<PropertyKey, unknown>) {
  // JAISIU-4498 — keep the browser tab title in sync with the chat the
  // tab is showing, so parallel tabs are distinguishable. Re-resolved
  // on every render because the title can change for reasons that do
  // NOT touch `sessionKey`: the gateway derives a chat's title from
  // its first user message some time AFTER the session opens, so the
  // title flips from "Chat 4" to "types vs files" while the operator
  // is still on the same session.
  applyDocumentTitle(
    resolveDocumentTitle({
      tab: host.tab,
      sessionKey: host.sessionKey,
      sessions: host.sessionsResult ?? null,
    }),
  );
  if (changed.has("chatRunId") || changed.has("chatSending")) {
    syncChatWaitTicker(host as unknown as Parameters<typeof syncChatWaitTicker>[0]);
    if (isFleetConsoleTab(host.tab)) {
      void maybePersistOrchestrationReports(host);
      void maybeInjectFleetCompletionNotice(host);
      void maybeInjectFileChangesNotices(host);
    }
  }
  if (
    isFleetConsoleTab(host.tab) &&
    (changed.has("chatMessages") ||
      changed.has("chatToolMessages") ||
      changed.has("chatStream") ||
      changed.has("chatLoading") ||
      changed.has("chatTranscriptLoading") ||
      changed.has("chatSubagents") ||
      changed.has("tab"))
  ) {
    const forcedByTab = changed.has("tab");
    const forcedByLoad =
      (changed.has("chatLoading") && changed.get("chatLoading") === true && !host.chatLoading) ||
      (changed.has("chatTranscriptLoading") &&
        changed.get("chatTranscriptLoading") === true &&
        !host.chatTranscriptLoading);
    scheduleChatScroll(
      host as unknown as Parameters<typeof scheduleChatScroll>[0],
      forcedByTab || forcedByLoad || !host.chatHasAutoScrolled,
    );
  }
  if (isFleetConsoleTab(host.tab) && (changed.has("sessionKey") || changed.has("tab"))) {
    void (
      host as unknown as { refreshChatWorkspace?: () => Promise<void> }
    ).refreshChatWorkspace?.();
    void (
      host as unknown as { refreshSessionEnvironment?: () => Promise<void> }
    ).refreshSessionEnvironment?.();
    void (
      host as unknown as { refreshChatHistorySessions?: () => Promise<void> }
    ).refreshChatHistorySessions?.();
  }
  if (isFleetConsoleTab(host.tab) && changed.has("connected") && host.connected) {
    void (
      host as unknown as { refreshSessionEnvironment?: () => Promise<void> }
    ).refreshSessionEnvironment?.();
    void (
      host as unknown as { refreshChatHistorySessions?: () => Promise<void> }
    ).refreshChatHistorySessions?.();
    // JAISIU-3474 — when the WS transitions to connected, ensure
    // the active chat's history is loaded AND the persisted scroll
    // anchor is restored. The `onHello` block in app-gateway.ts
    // fires the same loadChatHistory call, but the host element may
    // re-render between WS open and `onHello`, so the chat view can
    // briefly paint with an empty transcript. We await the load
    // here so the restore path runs in lock-step with the initial
    // paint, then call `restorePersistedScrollAnchor` to drain any
    // older pages needed to bring the anchor message into the DOM
    // and re-place the viewport.
    const connectHost = host as unknown as {
      loadChatHistory?: () => Promise<unknown>;
      loadScrollAnchorForActiveSession?: () => Promise<unknown>;
      sessionKey?: string;
    };
    void (async () => {
      await connectHost.loadScrollAnchorForActiveSession?.();
      await connectHost.loadChatHistory?.();
      if (
        (host as unknown as { sessionKey?: string }).sessionKey?.trim() ===
        connectHost.sessionKey?.trim()
      ) {
        const { restorePersistedScrollAnchor } = await import(
          /* @vite-ignore */ "./app-chat-cursor-parity.ts"
        );
        await restorePersistedScrollAnchor(
          host as unknown as Parameters<typeof restorePersistedScrollAnchor>[0],
        );
      }
    })();
  }
  // Per spec: when the chat-history sidebar is rendered but its session
  // data is missing (e.g. after a Vite HMR child remount that did not
  // re-fire `firstUpdated` and the `connected` watcher did not see a
  // change either), we must trigger a refresh so the sidebar does not
  // get stuck on "No chats yet" until a hard reload. The `firstUpdated`
  // hook already covers the normal first-paint case; this guard
  // catches the "host re-rendered with stale state" case.
  //
  // Guarded so we don't loop on every render: only fires when
  // `sessionsResult` is null AND we are connected AND on a chat tab
  // AND a fetch isn't already in flight. The `sessionsLoading` flag
  // in `refreshChatHistorySessions` provides the natural debounce.
  const appForHistory = host as unknown as {
    sessionsResult?: unknown;
    sessionsLoading?: boolean;
    refreshChatHistorySessions?: () => Promise<void>;
  };
  if (
    isFleetConsoleTab(host.tab) &&
    host.connected &&
    !appForHistory.sessionsResult &&
    !appForHistory.sessionsLoading
  ) {
    void appForHistory.refreshChatHistorySessions?.();
  }
  if (
    changed.has("chatSubagents") ||
    changed.has("agentsList") ||
    changed.has("sessionKey") ||
    changed.has("flociAgents")
  ) {
    const roster = buildAgentRoster({
      agentsList: host.agentsList,
      sessionKey: host.sessionKey,
      chatSubagents: host.chatSubagents,
      flociAgents: host.flociAgents,
    });
    host.agentRoster = roster;
  }
  if (changed.has("chatMessages")) {
    host.cacheActiveChatMessages?.();
  }
  if (isFleetConsoleTab(host.tab)) {
    const graphHost = host as unknown as {
      reapplyAgentFleetGraphLiveOverlay?: () => void;
      scheduleAgentFleetGraphRefresh?: (delayMs?: number) => void;
      refreshAgentFleetGraph?: () => Promise<void>;
    };
    if (changed.has("chatSubagents")) {
      if (host.sessionTabActivity) {
        host.sessionTabActivity = syncSubagentTabRunning(
          host.sessionTabActivity,
          host.chatSubagents,
        );
      }
      void maybePersistOrchestrationReports(host).then(() =>
        maybeInjectFleetCompletionNotice(host),
      );
      graphHost.reapplyAgentFleetGraphLiveOverlay?.();
    } else if (changed.has("tab") || changed.has("connected") || changed.has("sessionKey")) {
      if (changed.has("tab") || changed.has("connected")) {
        void graphHost.refreshAgentFleetGraph?.();
      } else {
        graphHost.scheduleAgentFleetGraphRefresh?.(500);
      }
    }
  }
  if (
    host.tab === "logs" &&
    (changed.has("logsEntries") || changed.has("logsAutoFollow") || changed.has("tab"))
  ) {
    if (host.logsAutoFollow && host.logsAtBottom) {
      scheduleLogsScroll(
        host as unknown as Parameters<typeof scheduleLogsScroll>[0],
        changed.has("tab") || changed.has("logsAutoFollow"),
      );
    }
  }
}

/** Drop every persisted answered-question entry from localStorage. We
 *  no longer show answered cards in the chat thread (the operator asked
 *  for plain-text conversation instead), and stale entries caused a
 *  gray-out bubble to reappear on every fresh load. */
function clearStaleFleetAnswerPersistence(): void {
  if (typeof localStorage === "undefined") {
    return;
  }
  try {
    const removeKeys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith("jaisiu.fleetAnswers")) {
        removeKeys.push(key);
      }
    }
    for (const key of removeKeys) {
      localStorage.removeItem(key);
    }
  } catch {
    // localStorage disabled — nothing to clear.
  }
}
